import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { obterFeedbacksPainel, responderFeedbackPainel } from "../services/api";

/**
 * 💬 O QUE OS CANDIDATOS CONTARAM — a aba que faltava no painel da empresa (20/09/2026).
 *
 * Nasceu de uma pergunta do Nelson: *"a empresa visualiza os feedbacks, correto? e pode dar uma resposta
 * nele"*. A resposta honesta era NÃO — não existia tela nenhuma. E isso virou um problema de justiça no
 * mesmo dia em que o feedback passou a contar na reputação: a empresa perdia ponto por uma resposta que
 * não conseguia ver nem contestar. Cobrar às cegas é exatamente o que o eaiRH cobra das empresas.
 *
 * 🔒 SEM NOME DE QUEM RESPONDEU. O feedback foi dado a NÓS, com a promessa de que a empresa veria o
 *    conteúdo e não o autor. Sem isso, "contar como foi" passa a ter custo pessoal para quem conta — e
 *    quem está procurando emprego não vai assumir esse custo. O retorno é sobre o processo, não sobre
 *    uma pessoa específica que "reclamou".
 */
const ETAPA = {
  Aguardando: "⏳ Aguardando", SemRetorno: "🔇 Sem retorno", ChamadoParaEntrevista: "📅 Chamado p/ entrevista",
  FezEntrevista: "🗣️ Fez entrevista", RecebeuNao: "✋ Recebeu um não", Contratado: "🎉 Contratado",
};

export default function FeedbacksEmpresa() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [respondendo, setRespondendo] = useState(null);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => { obterFeedbacksPainel().then(setDados).catch((e) => setErro(e.message)); }, []);

  const dia = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("pt-BR") : "—"; } catch { return "—"; } };
  const semRetorno = (f) => f.houveRetorno === false || f.retornoDeQuem === "eairh";

  async function enviar(f) {
    setEnviando(true); setErro("");
    try {
      const r = await responderFeedbackPainel(f.id, texto);
      setDados((d) => ({
        ...d,
        semResposta: Math.max(0, d.semResposta - 1),
        itens: d.itens.map((x) => (x.id === f.id
          ? { ...x, respostaDaEmpresa: texto, respostaEm: r.respostaEm } : x)),
      }));
      setRespondendo(null); setTexto("");
    } catch (e) { setErro(e.message); } finally { setEnviando(false); }
  }

  if (erro && !dados) return <p role="alert" className="text-sm text-red-600">{erro}</p>;
  if (!dados) return <p className="text-gray-500">Carregando...</p>;

  const itens = dados.itens.filter((f) =>
    filtro === "semRetorno" ? semRetorno(f)
    : filtro === "semResposta" ? !f.respostaDaEmpresa
    : true);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card v={dados.total} l="pessoas contaram como foi" cor="text-gray-800" />
        <Card v={dados.semRetorno} l="disseram que não tiveram retorno" cor="text-amber-600" destaque={dados.semRetorno > 0} />
        <Card v={dados.comRetorno} l="disseram que tiveram retorno" cor="text-green-600" />
        <Card v={dados.notaMedia ?? "—"} l="nota média que deram" cor="text-blue-600" />
      </div>

      {dados.total === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-600">
          <p className="font-semibold text-gray-900">Ninguém contou como foi ainda.</p>
          <p className="mt-1 text-sm">
            Depois que um processo termina, perguntamos ao candidato como foi. O que ele responder aparece aqui
            — e é o que forma a reputação da sua empresa no eaiRH.
          </p>
        </div>
      ) : (<>
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Você vê <b>o que a pessoa respondeu</b>, nunca o nome dela — o feedback é dado ao eaiRH com essa
          promessa. Se quiser contar a sua versão, responda: <b>quem escreveu recebe a sua resposta</b>.
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {[["todos", `Todos (${dados.total})`],
            ["semRetorno", `Disseram que não tiveram retorno (${dados.semRetorno})`],
            ["semResposta", `Você ainda não respondeu (${dados.semResposta})`]].map(([k, t]) => (
            <button key={k} onClick={() => setFiltro(k)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold ${filtro === k
                ? "border-primary bg-primary text-white"
                : "border-gray-200 bg-white text-gray-600 hover:text-gray-900"}`}>{t}</button>
          ))}
        </div>

        {erro && <p role="alert" className="mb-3 text-sm text-red-600">{erro}</p>}
        {itens.length === 0 && <p className="text-sm text-gray-500">Nada neste filtro.</p>}

        <div className="space-y-3">
          {itens.map((f) => (
            <div key={f.id} className={`rounded-xl border bg-white p-4 ${semRetorno(f) ? "border-amber-300" : "border-gray-200"}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/vagas/${f.vagaId}`} className="font-semibold text-gray-900 hover:text-primary">{f.vaga}</Link>
                {semRetorno(f) && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                    🔇 não teve retorno
                  </span>
                )}
                {f.nota != null && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">nota {f.nota}</span>
                )}
                {f.contratado && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800">🎉 contratado</span>}
                {f.etapa && <span className="text-[11px] text-gray-500">{ETAPA[f.etapa] || f.etapa}</span>}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                candidatou-se em {dia(f.candidatouEm)} · contou como foi em {dia(f.respondidoEm)}
              </p>

              {f.comentario && (
                <blockquote className="mt-2 border-l-4 border-gray-200 pl-3 text-sm text-gray-800 whitespace-pre-line">
                  {f.comentario}
                </blockquote>
              )}
              {!f.comentario && (
                <p className="mt-2 text-sm text-gray-500 italic">
                  A pessoa respondeu as perguntas, mas não escreveu um comentário.
                </p>
              )}

              {f.respostaDaEmpresa ? (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Sua resposta · {dia(f.respostaEm)}
                  </p>
                  <p className="mt-1 text-sm text-gray-800 whitespace-pre-line">{f.respostaDaEmpresa}</p>
                </div>
              ) : respondendo === f.id ? (
                <div className="mt-3">
                  <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} autoFocus
                    placeholder="Conte a sua versão. Quem escreveu o feedback vai receber isto."
                    className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-primary focus:outline-none" />
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => enviar(f)} disabled={enviando || texto.trim().length < 10}
                      className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                      {enviando ? "Enviando..." : "Responder"}
                    </button>
                    <button onClick={() => { setRespondendo(null); setTexto(""); }}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-600">Cancelar</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setRespondendo(f.id); setTexto(""); }}
                  className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                  Responder
                </button>
              )}
            </div>
          ))}
        </div>
      </>)}

      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        O que aparece aqui entra na reputação da sua empresa com peso pequeno — uma resposta isolada quase não
        muda o número, e é assim de propósito: num processo com 50 candidatos, 49 são descartados por definição.
        O que move a nota é o padrão, não o caso.
      </p>
    </>
  );
}

function Card({ v, l, cor, destaque }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 text-center ${destaque ? "border-amber-400 ring-1 ring-amber-200" : "border-gray-200"}`}>
      <div className={`text-2xl font-bold ${cor}`} style={{ fontVariantNumeric: "tabular-nums" }}>{v}</div>
      <div className="text-xs text-gray-500">{l}</div>
    </div>
  );
}
